import React from 'react';
import { Shield, AlertTriangle, CheckCircle, XCircle, Gauge } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';

interface BackpressureData {
  active: boolean;
  strategy: 'rate-limiting' | 'load-shedding' | 'circuit-breaker' | 'none';
  currentLimit: number;
  originalLimit: number;
  activeSince?: string;
  reason?: string;
}

interface BackpressureStatusProps {
  backpressure: BackpressureData;
  onApplyBackpressure: () => void;
  onReleaseBackpressure: () => void;
}

export const BackpressureStatus: React.FC<BackpressureStatusProps> = ({ 
  backpressure, 
  onApplyBackpressure, 
  onReleaseBackpressure 
}) => {
  const getStrategyIcon = (strategy: string) => {
    switch (strategy) {
      case 'rate-limiting':
        return <Gauge className="w-5 h-5 text-orange-500" />;
      case 'load-shedding':
        return <Shield className="w-5 h-5 text-red-500" />;
      case 'circuit-breaker':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <CheckCircle className="w-5 h-5 text-green-500" />;
    }
  };

  const getStrategyDescription = (strategy: string): string => {
    switch (strategy) {
      case 'rate-limiting':
        return 'Incoming message rate is being throttled to protect system stability';
      case 'load-shedding':
        return 'Low-priority messages are being dropped to maintain core functionality';
      case 'circuit-breaker':
        return 'Circuit breaker is open - all new messages are being rejected';
      default:
        return 'No backpressure strategies are currently active';
    }
  };

  const formatDuration = (activeSince: string): string => {
    const duration = Date.now() - new Date(activeSince).getTime();
    const minutes = Math.floor(duration / 60000);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    }
    return `${minutes}m`;
  };

  return (
    <Card className={backpressure.active ? 'border-orange-200 bg-orange-50' : ''}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {backpressure.active ? (
              <AlertTriangle className="w-6 h-6 text-orange-500" />
            ) : (
              <Shield className="w-6 h-6 text-green-500" />
            )}
            <div>
              <CardTitle>Backpressure Control</CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                {backpressure.active ? 'Active' : 'Inactive'}
              </p>
            </div>
          </div>
          {backpressure.active ? (
            <button
              onClick={onReleaseBackpressure}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
            >
              Release Backpressure
            </button>
          ) : (
            <button
              onClick={onApplyBackpressure}
              className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 transition-colors"
            >
              Apply Backpressure
            </button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {backpressure.active && (
            <>
              <div className="flex items-center space-x-3 p-3 bg-white rounded-lg">
                {getStrategyIcon(backpressure.strategy)}
                <div className="flex-1">
                  <p className="font-medium capitalize">
                    {backpressure.strategy.replace('-', ' ')}
                  </p>
                  <p className="text-sm text-gray-600">
                    {getStrategyDescription(backpressure.strategy)}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Current Limit</p>
                  <p className="text-lg font-semibold">{backpressure.currentLimit}/s</p>
                  <p className="text-xs text-gray-400">
                    {Math.round((backpressure.currentLimit / backpressure.originalLimit) * 100)}% of normal
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Active Duration</p>
                  <p className="text-lg font-semibold">
                    {backpressure.activeSince ? formatDuration(backpressure.activeSince) : 'N/A'}
                  </p>
                </div>
              </div>

              {backpressure.reason && (
                <div className="p-3 bg-orange-100 rounded">
                  <p className="text-sm font-medium text-orange-800">Trigger Reason</p>
                  <p className="text-sm text-orange-700 mt-1">{backpressure.reason}</p>
                </div>
              )}
            </>
          )}

          {!backpressure.active && (
            <div className="text-center py-4">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="text-gray-600">System is operating normally</p>
              <p className="text-sm text-gray-500 mt-1">
                No backpressure strategies are currently needed
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};